import { Box } from '@mui/joy';
import { isEmpty } from 'lodash';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { UseFormHandleSubmit, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext } from 'core/states';
import { enterPress } from 'core/utils';
import { PartSection, PricingSection } from 'parts/components';
import { useGetUoms } from 'parts/hooks';
import { IUom } from 'parts/types';

import { IAddPartLineFormData } from './types';
import { usePartValidation } from './usePartValidation';

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IAddPartLineFormData>) => void;
  onSubmit: (reset: UseFormReset<IAddPartLineFormData>) => (data: IAddPartLineFormData) => void;
}

export const AddLineFormFields = ({ isOpen, onClose, onSubmit }: IProps) => {
  const [uomOptions, setUomOptions] = useState<IUom[]>([]);
  const [verifiedPart, setVerifiedPart] = useState(false);
  const [standardCost, setStandardCost] = useState<number | null>(null);
  const [purchaseCost, setPurchaseCost] = useState<number | null>(null);

  const { t } = useTranslation();

  const { control, errors, setValue, getValues, reset, watch, handleSubmit } = useFormContext();
  const { setFocusElementName } = useFocusInputFunctionality();

  const { user } = useAuthUser();
  const { records: uoms } = useGetUoms({ filters: { bomDisplay: 1 } }, isOpen);

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ user }),
    [user]
  );

  const { isValidating, onChangePartNumber, onKeyDownPartNumber, onChangeManufacturer } =
    usePartValidation({
      setValue,
      getValues,
      reset,
      watch,
      setUomOptions,
      setVerifiedPart,
      setStandardCost,
      setPurchaseCost,
    });

  const [partValue, manufacturerValue, descriptionValue] = watch([
    'part',
    'manufacturer',
    'description',
  ]);

  const emptyPartValues = useMemo(
    () => isEmpty(partValue || manufacturerValue || descriptionValue),
    [partValue, manufacturerValue, descriptionValue]
  );

  useEffect(() => {
    if (isOpen) {
      setFocusElementName('part');
    }
  }, [isOpen, setFocusElementName]);

  useEffect(() => {
    if (uoms.length > 0) {
      setUomOptions(uoms);
    }
  }, [uoms, setUomOptions]);

  useEffect(() => {
    if (!emptyPartValues && !watch('uom') && uomOptions.length > 0) {
      setValue('uom', uomOptions?.[0]?.name);
    }
  }, [emptyPartValues, uomOptions, watch, setValue]);

  const onKeyDownQuantity = (
    event: KeyboardEvent<HTMLInputElement>,
    formHandleSubmit: UseFormHandleSubmit<IAddPartLineFormData>
  ) => {
    enterPress(event, formHandleSubmit(onSubmit(reset)));
  };

  const handleOnClose = (formReset: UseFormReset<IAddPartLineFormData>) => {
    setVerifiedPart(false);
    setUomOptions(uoms);
    setStandardCost(null);
    setPurchaseCost(null);
    onClose(formReset);
  };

  return (
    <Form
      isOpen={isOpen}
      onClose={handleOnClose}
      title={t('titleAddLine')}
      submitBtnLabel={t('buttonLabelSave')}
      onSubmit={onSubmit}
    >
      <PartSection
        onChangePartNumber={onChangePartNumber}
        onKeyDownPartNumber={onKeyDownPartNumber}
        onChangeManufacturer={onChangeManufacturer}
        isValidating={isValidating}
        verifiedPart={verifiedPart}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <FormField
          name="quantity"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control: control,
            error: errors.quantity,
            label: t('labelQuantity'),
            placeholder: t('placeholderQuantity'),
            onKeyDown: (event: KeyboardEvent<HTMLInputElement>) =>
              onKeyDownQuantity(event, handleSubmit),
            slotProps: {
              input: {
                min: 0,
              },
            },
            disabled: isValidating || emptyPartValues,
            required: true,
          }}
        />

        <FormField
          name="uom"
          type={InputType.SELECT}
          inputProps={{
            control: control,
            error: errors.uom,
            label: t('labelUom'),
            placeholder: t('placeholderUpsertLineUom'),
            getLabel: (option: IUom) => option?.name,
            getValue: (option: IUom) => option?.name,
            options: uomOptions,
            disabled: isValidating || emptyPartValues,
            required: true,
          }}
        />
      </Box>

      <PricingSection purchaseCost={purchaseCost} standardCost={standardCost} />
    </Form>
  );
};
